<template>
  <div
    class="product-chip critical-component-hide"
    :class="[
      $blockClass(blockClass, 'product-chip'),
      {
        [$class('loading')]: loading,
        [$class('active')]: isActive,
        'product-chip--small': small
      },
    ]"
    @click="handleClick"
  >
    <div class="product-chip__media">
      <div
        v-if="showNumber && isActive"
        class="product-chip__number"
        v-text="number"
      />

      <responsive-image
        v-if="image"
        :class="$blockClass(blockClass, 'product-chip-image')"
        :force-update="true"
        :image="image"
        :lazy-load="false"
        :style="{ animationDelay: animationDelay }"
        block-class="product-chip"
        min-max="80-240"
        ratio="1:1"
        sizes="80px"
      />
    </div>

    <div
      v-if="!gridLayout"
      class="product-chip__body-wrapper"
    >
      <div
        :class="{[$class('grid')]: gridLayout}"
        class="product-chip__body"
      >
        <component
          :is="url ? 'a' : 'div'"
          v-if="productTitle"
          :class="$blockClass(blockClass, 'product-chip-title')"
          :style="{ animationDelay: animationDelay }"
          class="product-chip__title"
          :href="url"
          v-text="productTitle"
        />

      <div
        v-if="formattedVariantTitle && showVariantTitle"
        class="product-chip__variant-title caption"
        :class="$blockClass(blockClass, 'product-chip-variant-title')"
        :style="{ animationDelay: animationDelay }"
        v-text="formattedVariantTitle"
      />

        <product-prices
          v-if="showPrice && variant"
          :compare-at-price="variant.compareAtPrice?.amount || variant.compareAtPrice"
          :price="variant.price?.amount || variant.price"
          :reverse="true"
          block-class="product-chip"
          class="body-2"
        />
      </div>

      <slot name="customBody" />
    </div>

    <div
      v-if="showCheckbox"
      :class="{ [$class('active')]: isActive }"
      class="checkbox checkbox--medium-border"
    >
      <span class="checkbox__label">
        <span
          class="visually-hidden"
          v-text="$string('bedVisualizer.accessibility.addon')"
        />
      </span>
    </div>

    <slot name="absoluteElement" />
  </div>
</template>

<script>

/**
 * Vue: Product chip (product-chip)
 * -----------------------------------------------------------------------------
 * Displays contents of product chip.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean|Object} image - Image object.
 * @param {Number} index - Product chip's forloop index.
 * @param {Boolean} isActive - Product chip's active state.
 * @param {Number} number - Selection index.
 * @param {Boolean} showNumber - Show product chip selection index.
 * @param {Boolean} loading - Product chip's loading state.
 * @param {String} productTitle - Product title.
 * @param {Object} variant - Product variant.
 */
import ResponsiveImage from '~global/images/responsive-image'
import ProductPrices from '~global/product-prices/product-prices'

import { values as timings } from '~/config/timings'

export default {
  name: 'ProductChip',

  components: {
    ResponsiveImage,
    ProductPrices,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    image: {
      type: [Boolean, Object],
      default: false,
    },
    index: {
      type: Number,
      default: 0,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    gridLayout: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    number: {
      type: Number,
      default: 0,
    },
    showNumber: {
      type: Boolean,
      default: false,
    },
    showPrice: {
      type: Boolean,
      default: false,
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    showVariantTitle: {
      type: Boolean,
      default: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    url: {
      type: [Boolean, String],
      default: false,
    },
    productTitle: {
      type: String,
      default: '',
    },
    variant: {
      type: [Boolean, Object],
      default: () => ({}),
    },
  },

  computed: {

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return `${this.index * timings.xs}s`
    },

    /**
     * Computed option values.
     * @returns {Array|Boolean}
     */
    optionValues() {
      return this.variant?.selectedOptions?.map(option => option.value)
    },

    /**
     * Format variant to be separated by pipe.
     * @returns {String}
     */
    formattedVariantTitle() {
      const isDefault = this.variant?.title?.toLowerCase() === 'default title'

      if (isDefault) {
        return ''
      }

      return this.optionValues?.join(' | ') || this.variant?.title || ''
    },
  },
}
</script>

<style lang="scss">
@import './product-chip';
</style>
